import { useState } from 'react';
import { Download, Loader2, RefreshCw } from 'lucide-react';
import { Button, Card, CardTitle, Chip, Modal } from '@/ui/primitives';
import { Stack } from '@/ui/layout';
import { ApiError } from '@/lib/api-client';
import { NIVEL_LABEL, filterVerificacoes, nivelTone, sortVerificacoes } from '../data';
import { useQueryClient } from '@tanstack/react-query';
import { useDiagnosticoDetail, useOnr, useVarrerCliente } from '../api';
import type { OnrItem } from '../schemas';
import { ImovelCard } from './ImovelCard';
import { VarreduraClienteProgressModal } from './VarreduraClienteProgressModal';
import { VerificationRow } from './VerificationRow';

interface Props {
  cpf: string | null;
  onClose: () => void;
}

export function DiagnosticoDetailModal({ cpf, onClose }: Props) {
  const { data, isLoading, error } = useDiagnosticoDetail(cpf);
  const { data: onrData } = useOnr(cpf);
  const varrer = useVarrerCliente();
  const qc = useQueryClient();
  // varreduraOpen é a única fonte de verdade — abre no onSuccess da mutation,
  // fecha no botão Fechar do modal de progresso (ou via onFinished automático).
  const [varreduraOpen, setVarreduraOpen] = useState(false);

  const onrByImovelId = new Map<string, OnrItem>(
    onrData?.imoveis.map((i) => [String(i.id), i]) ?? [],
  );

  const open = cpf !== null;
  const title = data ? data.nome : 'Carregando…';

  const onPdfClick = () => {
    if (!cpf) return;
    window.open(`/api/v2/diagnostico/${cpf}/relatorio-pdf`, '_blank', 'noreferrer');
  };

  return (
    <Modal
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title={title}
      size="2xl"
      footer={
        cpf ? (
          <>
            <Button variant="secondary" size="md" onClick={onPdfClick}>
              <Download size={14} strokeWidth={1.5} />
              Relatório PDF
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={() =>
                varrer.mutate(cpf, {
                  onSuccess: () => setVarreduraOpen(true),
                })
              }
              disabled={varrer.isPending || varreduraOpen}
            >
              {varrer.isPending || varreduraOpen ? (
                <Loader2 size={14} strokeWidth={1.5} className="animate-spin" />
              ) : (
                <RefreshCw size={14} strokeWidth={1.5} />
              )}
              {varrer.isPending
                ? 'Disparando…'
                : varreduraOpen
                  ? 'Em andamento…'
                  : 'Rodar varredura'}
            </Button>
          </>
        ) : null
      }
    >
      {isLoading && <p className="text-sm text-text-muted">Carregando diagnóstico…</p>}
      {error && (
        <p className="text-sm text-danger">
          {error instanceof ApiError ? error.message : 'Falha ao carregar diagnóstico.'}
        </p>
      )}

      {data && cpf && (
        <Stack gap="lg">
          {/* Cabeçalho */}
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-text-2 tabular-nums">{data.cpf}</span>
            {data.municipio && (
              <Chip tone="neutral" size="sm">
                {data.municipio}
              </Chip>
            )}
            {data.nivel && (
              <Chip tone={nivelTone(data.nivel)} size="sm" dot>
                {NIVEL_LABEL[data.nivel]} · Score {(data.score ?? 0).toFixed(0)}%
              </Chip>
            )}
            <Chip tone="neutral" size="sm">
              {data.total_imoveis} imóveis
            </Chip>
          </div>

          {!varreduraOpen && varrer.isSuccess && (
            <p className="text-xs text-ok">
              Varredura concluída. Os resultados foram atualizados.
            </p>
          )}
          {varrer.isError && (
            <p className="text-xs text-danger">
              {varrer.error instanceof ApiError
                ? `Falha ao disparar varredura: ${varrer.error.message}`
                : 'Falha ao disparar varredura. Tente novamente.'}
            </p>
          )}

          {/* Pessoa Física */}
          <Card padding="md">
            <Stack gap="sm">
              <div className="flex items-center justify-between">
                <CardTitle>Pessoa Física</CardTitle>
                {data.pessoa.score != null && data.pessoa.nivel && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold tabular-nums">
                      {data.pessoa.score.toFixed(0)}%
                    </span>
                    <Chip tone={nivelTone(data.pessoa.nivel)} size="sm" dot>
                      {NIVEL_LABEL[data.pessoa.nivel]}
                    </Chip>
                  </div>
                )}
              </div>
              {data.pessoa.verificacoes.length === 0 ? (
                <p className="text-xs text-text-muted">Sem verificações da pessoa.</p>
              ) : (
                <ul className="flex flex-col">
                  {sortVerificacoes(filterVerificacoes(data.pessoa.verificacoes)).map((v, i) => (
                    <VerificationRow
                      key={`p-${v.verificacao}-${i}`}
                      verification={v}
                      cpf={cpf}
                      imovelId={null}
                    />
                  ))}
                </ul>
              )}
            </Stack>
          </Card>

          {/* Imóveis — cada um traz seu próprio Registro Eletrônico de Imóveis (ex-ONR) inline */}
          <Stack gap="sm">
            <CardTitle>Imóveis</CardTitle>
            {data.imoveis.length === 0 ? (
              <Card padding="md">
                <p className="text-xs text-text-muted">Cliente sem imóveis cadastrados.</p>
              </Card>
            ) : (
              <Stack gap="sm">
                {data.imoveis.map((im) => (
                  <ImovelCard
                    key={im.imovel_id}
                    imovel={im}
                    cpf={cpf}
                    onr={onrByImovelId.get(String(im.imovel_id)) ?? null}
                  />
                ))}
              </Stack>
            )}
          </Stack>
        </Stack>
      )}
      <VarreduraClienteProgressModal
        open={varreduraOpen}
        cpf={cpf}
        onClose={() => setVarreduraOpen(false)}
        onFinished={() => {
          if (cpf) {
            void qc.invalidateQueries({ queryKey: ['diagnostico', 'detail', cpf] });
            void qc.invalidateQueries({ queryKey: ['diagnostico', 'list'] });
          }
        }}
      />
    </Modal>
  );
}
